import {
  createStyles,
  useTheme,
  withStyles,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Hidden,
  getEnabledTranslationLangs,
  useToggle
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, AlertDialogProps, DialogProps } from '@paintscout/ui'
import { Theme as ThemeOverride, useClientOptions } from '@paintscout/ui-business'
import { getObjectLabels, getQuotePresentation } from '@paintscout/util/builder'
import {
  Presentation,
  PresentationNavContextProvider,
  PresentationNavSide,
  PresentationNav,
  QuoteContextProvider,
  useQuote
} from '@paintscout/react-quote'
import { useSnackbar } from 'notistack'
import type { QuoteDocument } from 'paintscout'
import React, { useState } from 'react'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface QuotePreviewDialogProps extends Omit<AlertDialogProps, 'message' | 'title' | 'onConfirm'> {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  view: 'quote' | 'work-order' | 'archived-items' | 'activity' | 'details' | 'terms'
  onConfirm: (pending?: boolean, quote?: QuoteDocument) => void
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    dialogContent: {
      padding: 0,
      paddingTop: theme.paintscout.header.height.md,
      [theme.breakpoints.down('lg')]: {
        paddingTop: theme.paintscout.header.height.lg
      }
    },
    mainLayout: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',

      margin: `0 auto`,

      width: 1150,
      [theme.breakpoints.down('lg')]: {
        width: '100%'
      },
      '@media print': {
        width: 'auto'
      }
    },
    sideNavWrapper: {
      width: 175,
      marginTop: theme.spacing(4),
      marginRight: theme.spacing(2),
      '@media print': {
        display: 'none'
      }
    },
    sideNav: {
      width: 165,
      position: 'fixed'
    },
    presentationWrapper: {
      paddingBottom: 20,
      width: 960,
      [theme.breakpoints.down('lg')]: {
        width: '100%'
      },
      '@media print': {
        width: 'auto',
        padding: 0
      }
    }
  })
function QuotePreviewDialog(props: QuotePreviewDialogProps) {
  const { onConfirm, onClose, classes, view, ...baseDialogProps } = props
  const { options } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const { quote, estimator, isSaving, wisetackPromo, onItemAction } = useQuote()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const { value: saving, setValue: setSaving } = useToggle(isSaving)
  const theme = useTheme()
  const { translations } = useFlags()
  const availableLangs = getEnabledTranslationLangs(translations)
  const [presentationPage, setPresentationPage] = useState(null)
  const presentation = getQuotePresentation({ options, quote, isWorkOrder: false, isProductOrderForm: false })

  const [navOpen, setNavOpen] = useState(false)

  const scrollablePages = presentation?.advanced?.infiniteScroll ?? false

  const handleAccept = async (_: React.MouseEvent, pending?: boolean) => {
    if (!quote._rev) {
      enqueueSnackbar(`Please save the ${objectLabels.quote.value.toLowerCase()} before accepting.`, {
        variant: 'error'
      })
    } else {
      setSaving(true)
      await onConfirm(pending, quote)
      setSaving(false)
    }
  }

  return (
    <ThemeOverride customerFacing>
      <Dialog open={true} forceFullScreen={true} scroll="paper" fullWidth={true} maxWidth="lg" {...baseDialogProps}>
        <QuoteContextProvider
          onAccept={onConfirm}
          quote={quote}
          estimator={estimator}
          contextValue={{ wisetackPromo, isPresentation: true, onItemAction }}
        >
          <PresentationNavContextProvider
            open={navOpen}
            onScroll={setPresentationPage}
            currentPage={presentationPage}
            view={view}
            onAcceptClick={handleAccept}
            onPageClick={handlePageClick}
            onSectionClick={handleSectionClick}
            onOpen={() => setNavOpen(true)}
            onClose={() => setNavOpen(false)}
            isResponding={saving}
            availableLangs={availableLangs}
          >
            <PresentationNav breakpoint="lg" classes={{}} />
            <DialogContent classes={{ root: classes.dialogContent }} id="quote-preview-dialog-content">
              <div className={classes.mainLayout}>
                <Hidden lgDown={true}>
                  <div className={classes.sideNavWrapper}>
                    <PresentationNavSide classes={{ root: classes.sideNav }} />
                  </div>
                </Hidden>

                <div className={classes.presentationWrapper}>
                  <Presentation
                    stacked={scrollablePages}
                    presentation={presentation}
                    classes={{}}
                    page={presentationPage}
                    isPreview
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions centeredButton={true}>
              <Button type="submit" variant={'text'} onClick={() => onClose(null, null)}>
                Back to {objectLabels.quote.value}
              </Button>
            </DialogActions>
          </PresentationNavContextProvider>
        </QuoteContextProvider>
      </Dialog>
    </ThemeOverride>
  )

  function handleSectionClick(ev: React.MouseEvent, key: string) {
    setNavOpen(false)
    setPresentationPage(key)
    const dialogContentElement = document.getElementById('quote-preview-dialog-content')
    if (dialogContentElement) {
      const section = document.getElementById(key)
      const sectionOffset = section?.offsetTop ?? 0

      const spacing = theme.spacing(4)

      const header = document.getElementsByTagName('header')
      const headerHeight = header?.[0]?.offsetHeight ?? 0
      dialogContentElement.scrollTo({ top: sectionOffset - headerHeight - spacing, behavior: 'smooth' })
    }
  }

  function handlePageClick(ev: React.MouseEvent, key: string) {
    setPresentationPage(key)
    setNavOpen(false)
    const dialogContentElement = document.getElementById('quote-preview-dialog-content')
    if (presentation?.advanced?.infiniteScroll) {
      const firstSection = document.getElementById(key)
      if (firstSection) {
        const sectionOffset = firstSection?.offsetTop ?? 0

        const spacing = theme.spacing(4)

        const header = document.getElementsByTagName('header')
        const headerHeight = header?.[0]?.offsetHeight ?? 0
        dialogContentElement.scrollTo({ top: sectionOffset - headerHeight - spacing, behavior: 'smooth' })
      }
    } else {
      dialogContentElement.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}

export default withStyles(styles)(QuotePreviewDialog)
